import { cva, type VariantProps } from "class-variance-authority";
import { CheckIcon } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "~/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { cn } from "~/lib/utils";

import { Icon } from "../Icon";

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva(
  "m-1 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300",
  {
    variants: {
      variant: {
        default: "border-foreground/10 text-foreground bg-card hover:bg-card/80",
        secondary:
          "border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        inverted: "inverted",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  /**
   * An array of option objects to be displayed in the multi-select component.
   * Each option object has a label, value, and an optional icon.
   */
  options: {
    /** The text to display for the option. */
    label: string;
    /** The unique value associated with the option. */
    value: string;
    /** Optional icon component to display alongside the option. */
    icon?: React.ComponentType<{ className?: string }>;
  }[];

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void;

  /** The default selected values when the component mounts. */
  defaultValue?: string[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number;

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  container?: HTMLElement | null;

  label?: string;

  value?: string[];
}

export const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      onValueChange,
      value,
      defaultValue = [],
      placeholder = "Select options",
      label = "",
      maxCount = 3,
      modalPopover = false,
      className,
      container,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = React.useState<string[]>(defaultValue);
    const selectedValues = isControlled ? value! : internalValue;
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const [triggerWidth, setTriggerWidth] = React.useState<number>(0);

    React.useImperativeHandle(ref, () => triggerRef.current!, []);

    const handleChange = (newValues: string[]) => {
      if (!isControlled) {
        setInternalValue(newValues);
      }
      onValueChange(newValues);
    };

    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

    React.useEffect(() => {
      if (triggerRef.current) {
        setTriggerWidth(triggerRef.current.offsetWidth);
      }
    }, [selectedValues, label]);

    const toggleOption = (option: string) => {
      const newSelectedValues = selectedValues.includes(option)
        ? selectedValues.filter((value) => value !== option)
        : [...selectedValues, option];
      handleChange(newSelectedValues);
    };

    const handleClear = () => {
      handleChange([]);
    };

    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev);
    };

    const toggleAll = () => {
      if (selectedValues.length === options.length) {
        handleClear();
      } else {
        const allValues = options.map((option) => option.value);
        handleChange(allValues);
      }
    };

    return (
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal={modalPopover}>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            onClick={handleTogglePopover}
            className={cn(
              "flex h-12 min-w-40 items-center justify-between border border-neutral-200 bg-white px-3 py-0.5 transition-all hover:bg-violet-50",
              isPopoverOpen ? "rounded-b-none rounded-t-xl border-b-transparent" : "rounded-xl",
              className,
            )}
          >
            <div className="flex flex-1 flex-col items-start">
              <span className="details text-neutral-800">{label}</span>
              {selectedValues.length ? (
                <div className="flex flex-row">
                  <span className="body-sm text-black">
                    {selectedValues.slice(0, maxCount).map((value) => {
                      const option = options.find((o) => o.value === value);

                      if (!option) return null;

                      return option.label;
                    })}
                  </span>
                  {selectedValues.length > maxCount && (
                    <span className="details ml-2 rounded bg-neutral-200 px-1 py-0.5 text-black">
                      {`${t("searchLessonView.filterCount", { count: selectedValues.length - maxCount })}`}
                    </span>
                  )}
                </div>
              ) : (
                <span className="body-sm shrink-0 text-black">{placeholder}</span>
              )}
            </div>
            <div className="flex w-6 items-center justify-center">
              <Icon name="CHEVRONDOWN" className="size-6 text-black" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="z-[160] !rounded-b-xl rounded-t-none border-t-0 p-0"
          style={{ width: triggerWidth }}
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
          sideOffset={-1}
          container={container}
        >
          <Command>
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandSeparator />
              <CommandGroup className="px-0">
                {options.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => toggleOption(option.value)}
                      className={cn(
                        "flex min-h-8 cursor-pointer items-center justify-between rounded-none px-3 py-2 hover:bg-violet-50",
                        isSelected ? "bg-violet-100 text-violet-950" : "bg-white text-black",
                      )}
                    >
                      <span className="body-sm flex-1 text-left">{option.label}</span>
                      <div className="ml-2 flex flex-shrink-0 items-center justify-center">
                        {isSelected && (
                          <CheckIcon className="!size-6 text-violet-950" strokeWidth={2} />
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
                <CommandItem
                  key="all"
                  onSelect={toggleAll}
                  className="flex min-h-8 cursor-pointer items-center justify-between px-3 py-2 hover:bg-violet-50"
                >
                  <span className="body-sm">{placeholder}</span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);

MultiSelect.displayName = "MultiSelect";
